import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import Marquee from "react-fast-marquee";
import CalendarSection from "../components/CalendarSection";
import VisualEditor from "../components/VisualEditor";
import {
  ChartLine,
  Robot,
  Users,
  Calendar,
  Gear,
  SignOut,
  List,
  CaretUp,
  CaretDown,
  Lightning,
  TrendUp,
  Eye,
  CursorClick,
  Target,
  InstagramLogo,
  FacebookLogo,
  TiktokLogo,
  LinkedinLogo,
  XLogo,
  PaintBrush,
} from "@phosphor-icons/react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sidebar Component
const PlatformSidebar = ({ isOpen, setIsOpen }) => {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { name: "Dashboard", path: "/platform", icon: ChartLine },
    { name: "Generador IA", path: "/platform/ai", icon: Robot },
    { name: "CRM", path: "/platform/crm", icon: Users },
    { name: "Calendario", path: "/platform/calendar", icon: Calendar },
    { name: "Editor Visual", path: "/platform/editor", icon: PaintBrush },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#0A0A0A] border-r border-zinc-800 flex flex-col z-40" data-testid="platform-sidebar">
      {/* Logo */}
      <div className="p-6 border-b border-zinc-800">
        <Link to="/" className="block">
          <img src="/logo-vibrant.png" alt="klyvora7" className="h-12 w-auto" />
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                isActive
                  ? "bg-white text-black font-medium"
                  : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
              }`}
              data-testid={`sidebar-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Icon size={20} weight={isActive ? "fill" : "regular"} />
              {item.name}
            </Link>
          );
        })}
      </nav>

      {/* Settings & Logout */}
      <div className="p-4 border-t border-zinc-800 space-y-1">
        <Link
          to="/platform/settings"
          className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-all"
        >
          <Gear size={20} />
          Configuración
        </Link>
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:text-red-400 hover:bg-red-500/10 w-full transition-all"
          data-testid="platform-logout"
        >
          <SignOut size={20} />
          Cerrar Sesión
        </button>
      </div>
    </aside>
  );
};

// Metric Ticker for Header
const MetricTicker = () => {
  const metrics = [
    { label: "Engagement", value: "+12.5%", positive: true },
    { label: "Alcance", value: "48.2K", positive: true },
    { label: "CTR", value: "3.8%", positive: true },
    { label: "Conversiones", value: "+8.3%", positive: true },
    { label: "ROI", value: "4.2x", positive: true },
    { label: "Followers", value: "+234", positive: true },
  ];

  return (
    <Marquee gradient={false} speed={40} className="bg-[#13151A] border-b border-zinc-800 py-2">
      {metrics.map((metric, i) => (
        <div key={i} className="flex items-center gap-2 mx-8">
          <span className="text-zinc-500 text-xs font-mono">{metric.label}</span>
          <span className={`text-sm font-mono font-medium ${metric.positive ? "text-emerald-400" : "text-red-400"}`}>
            {metric.value}
          </span>
          {metric.positive ? (
            <CaretUp size={14} className="text-emerald-400" weight="fill" />
          ) : (
            <CaretDown size={14} className="text-red-400" weight="fill" />
          )}
        </div>
      ))}
    </Marquee>
  );
};

// Dashboard Page
const DashboardPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/platform/analytics`, { withCredentials: true });
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Error al cargar analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const overview = analytics?.overview || {};
  const dailyData = analytics?.daily_data || [];
  const channels = analytics?.channels || [];

  const StatCard = ({ label, value, change, icon: Icon }) => (
    <div className="bg-[#13151A] border border-zinc-800 rounded-lg p-6" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{label}</span>
        <Icon size={20} className="text-zinc-600" />
      </div>
      <div className="text-3xl font-medium text-white font-mono mb-2">
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
      <div className={`flex items-center gap-1 text-sm ${change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
        {change >= 0 ? <CaretUp size={16} weight="fill" /> : <CaretDown size={16} weight="fill" />}
        <span>{Math.abs(change)}%</span>
        <span className="text-zinc-500 ml-1">vs mes anterior</span>
      </div>
    </div>
  );

  const platformIcons = {
    Instagram: InstagramLogo,
    Facebook: FacebookLogo,
    TikTok: TiktokLogo,
    LinkedIn: LinkedinLogo,
    X: XLogo,
  };

  return (
    <div className="space-y-6" data-testid="platform-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium text-white">Dashboard</h1>
          <p className="text-zinc-500 text-sm">Resumen de rendimiento en tiempo real</p>
        </div>
        <button className="px-4 py-2 bg-white text-black rounded-lg font-medium text-sm hover:bg-zinc-200 transition-colors">
          Exportar Reporte
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Engagement" value={overview.total_engagement} change={overview.engagement_change} icon={Lightning} />
        <StatCard label="Alcance" value={overview.total_reach} change={overview.reach_change} icon={Eye} />
        <StatCard label="Clicks" value={overview.total_clicks} change={overview.clicks_change} icon={CursorClick} />
        <StatCard label="Conversiones" value={overview.total_conversions} change={overview.conversions_change} icon={Target} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Chart */}
        <div className="lg:col-span-2 bg-[#13151A] border border-zinc-800 rounded-lg p-6">
          <h3 className="text-lg font-medium text-white mb-6">Engagement (30 días)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={dailyData}>
              <defs>
                <linearGradient id="engagementGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2A2D35" />
              <XAxis dataKey="date" stroke="#71717A" tick={{ fill: "#71717A", fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis stroke="#71717A" tick={{ fill: "#71717A", fontSize: 11 }} />
              <Tooltip
                contentStyle={{ backgroundColor: "#13151A", border: "1px solid #2A2D35", borderRadius: "8px" }}
                labelStyle={{ color: "#fff" }}
                itemStyle={{ color: "#10B981" }}
              />
              <Area type="monotone" dataKey="engagement" stroke="#10B981" strokeWidth={2} fill="url(#engagementGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* KPIs */}
        <div className="bg-[#13151A] border border-zinc-800 rounded-lg p-6">
          <h3 className="text-lg font-medium text-white mb-6">KPIs Clave</h3>
          <div className="space-y-6">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-zinc-400 text-sm">CTR</span>
                <span className="text-white font-mono">{overview.ctr}%</span>
              </div>
              <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(overview.ctr * 10, 100)}%` }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-zinc-400 text-sm">Tasa de Conversión</span>
                <span className="text-white font-mono">{overview.conversion_rate}%</span>
              </div>
              <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(overview.conversion_rate * 5, 100)}%` }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-zinc-400 text-sm">ROI</span>
                <span className="text-white font-mono text-2xl">{overview.roi}x</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Channels */}
      <div className="bg-[#13151A] border border-zinc-800 rounded-lg p-6">
        <h3 className="text-lg font-medium text-white mb-6">Rendimiento por Canal</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {channels.map((channel) => {
            const Icon = platformIcons[channel.name] || ChartLine;
            return (
              <div key={channel.name} className="p-4 bg-[#0A0A0A] border border-zinc-800 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <Icon size={24} className="text-zinc-400" />
                  <span className="text-white font-medium">{channel.name}</span>
                </div>
                <div className="text-2xl font-mono text-white mb-1">{channel.followers.toLocaleString()}</div>
                <div className={`text-sm ${channel.growth >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  +{channel.growth}% este mes
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// AI Generator Page
const AIGeneratorPage = () => {
  const [contentType, setContentType] = useState("copy");
  const [businessType, setBusinessType] = useState("");
  const [tone, setTone] = useState("professional");
  const [context, setContext] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/platform/ai/history`, { withCredentials: true });
      setHistory(response.data);
    } catch (error) {
      console.error("Error fetching history");
    }
  };

  const generateContent = async () => {
    if (!businessType.trim()) {
      toast.error("Ingresa el tipo de negocio");
      return;
    }
    setLoading(true);
    setResult("");

    try {
      const response = await axios.post(
        `${API}/platform/ai/generate`,
        { content_type: contentType, business_type: businessType, tone, context },
        { withCredentials: true }
      );
      setResult(response.data.result);
      fetchHistory();
      toast.success("Contenido generado!");
    } catch (error) {
      toast.error("Error al generar contenido");
    } finally {
      setLoading(false);
    }
  };

  const contentTypes = [
    { id: "copy", label: "Copies Publicitarios", icon: "📝" },
    { id: "caption", label: "Captions Virales", icon: "💬" },
    { id: "hashtags", label: "Hashtags", icon: "#️⃣" },
    { id: "campaign", label: "Campaña Completa", icon: "🚀" },
  ];

  const tones = [
    { id: "professional", label: "Profesional" },
    { id: "casual", label: "Casual" },
    { id: "funny", label: "Divertido" },
    { id: "urgent", label: "Urgente" },
    { id: "luxury", label: "Lujo" },
  ];

  return (
    <div className="space-y-6" data-testid="ai-generator-page">
      <div>
        <h1 className="text-2xl font-medium text-white">Generador de Contenido IA</h1>
        <p className="text-zinc-500 text-sm">Crea copies, captions y hashtags optimizados con IA</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Generator Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Content Type Selection */}
          <div className="bg-[#13151A] border border-zinc-800 rounded-lg p-6">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-zinc-500 mb-4">Tipo de Contenido</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {contentTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setContentType(type.id)}
                  className={`p-4 rounded-lg border text-left transition-all ${
                    contentType === type.id
                      ? "bg-white text-black border-white"
                      : "bg-[#0A0A0A] text-zinc-400 border-zinc-800 hover:border-zinc-600"
                  }`}
                  data-testid={`content-type-${type.id}`}
                >
                  <div className="text-2xl mb-2">{type.icon}</div>
                  <div className="text-sm font-medium">{type.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Form Fields */}
          <div className="bg-[#13151A] border border-zinc-800 rounded-lg p-6 space-y-4">
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Tipo de Negocio *</label>
              <input
                type="text"
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value)}
                placeholder="Ej: Restaurante italiano, Tienda de ropa, Clínica dental..."
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-zinc-800 rounded-lg text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-white"
                data-testid="business-type-input"
              />
            </div>

            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Tono</label>
              <div className="flex flex-wrap gap-2">
                {tones.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTone(t.id)}
                    className={`px-4 py-2 rounded-full text-sm transition-all ${
                      tone === t.id
                        ? "bg-white text-black"
                        : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Contexto adicional (opcional)</label>
              <textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="Ej: Promoción de verano, nuevo producto, evento especial..."
                rows={3}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-zinc-800 rounded-lg text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-white resize-none"
                data-testid="context-input"
              />
            </div>

            <button
              onClick={generateContent}
              disabled={loading}
              className="w-full py-4 bg-white text-black rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-zinc-200 transition-colors disabled:opacity-50"
              data-testid="generate-button"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : (
                <>
                  <Robot size={20} weight="fill" />
                  Generar Contenido
                </>
              )}
            </button>
          </div>

          {/* Result */}
          {result && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#13151A] border border-zinc-800 rounded-lg p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold uppercase tracking-widest text-zinc-500">Resultado</h3>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(result);
                    toast.success("Copiado al portapapeles");
                  }}
                  className="text-xs text-zinc-400 hover:text-white"
                >
                  Copiar
                </button>
              </div>
              <div className="prose prose-invert max-w-none">
                <pre className="whitespace-pre-wrap text-zinc-300 text-sm font-sans leading-relaxed">{result}</pre>
              </div>
            </motion.div>
          )}
        </div>

        {/* History */}
        <div className="bg-[#13151A] border border-zinc-800 rounded-lg p-6 h-fit">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-zinc-500 mb-4">Historial</h3>
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {history.length === 0 ? (
              <p className="text-zinc-500 text-sm">Sin historial aún</p>
            ) : (
              history.slice(0, 10).map((item, i) => (
                <div
                  key={i}
                  className="p-3 bg-[#0A0A0A] border border-zinc-800 rounded-lg cursor-pointer hover:border-zinc-600 transition-colors"
                  onClick={() => setResult(item.result)}
                >
                  <div className="text-xs text-zinc-500 mb-1">{item.content_type}</div>
                  <div className="text-sm text-white truncate">{item.business_type}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// CRM Page
const CRMPage = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newLead, setNewLead] = useState({ name: "", email: "", phone: "", company: "", source: "website", value: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/platform/crm/leads`, { withCredentials: true }),
        axios.get(`${API}/platform/crm/stats`, { withCredentials: true }),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Error al cargar CRM");
    } finally {
      setLoading(false);
    }
  };

  const addLead = async () => {
    if (!newLead.name || !newLead.email) {
      toast.error("Nombre y email son requeridos");
      return;
    }
    try {
      await axios.post(`${API}/platform/crm/leads`, newLead, { withCredentials: true });
      toast.success("Lead agregado");
      setShowAddModal(false);
      setNewLead({ name: "", email: "", phone: "", company: "", source: "website", value: 0 });
      fetchData();
    } catch (error) {
      toast.error("Error al agregar lead");
    }
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await axios.patch(`${API}/platform/crm/leads/${leadId}`, { status }, { withCredentials: true });
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const statuses = [
    { id: "new", label: "Nuevo", color: "bg-blue-500" },
    { id: "contacted", label: "Contactado", color: "bg-yellow-500" },
    { id: "qualified", label: "Calificado", color: "bg-purple-500" },
    { id: "proposal", label: "Propuesta", color: "bg-orange-500" },
    { id: "won", label: "Ganado", color: "bg-emerald-500" },
    { id: "lost", label: "Perdido", color: "bg-red-500" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="crm-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium text-white">CRM</h1>
          <p className="text-zinc-500 text-sm">Gestiona tus leads y oportunidades</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-white text-black rounded-lg font-medium text-sm hover:bg-zinc-200 transition-colors"
          data-testid="add-lead-button"
        >
          + Agregar Lead
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[#13151A] border border-zinc-800 rounded-lg p-4">
          <div className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Total Leads</div>
          <div className="text-3xl font-mono text-white">{stats?.total_leads || 0}</div>
        </div>
        <div className="bg-[#13151A] border border-zinc-800 rounded-lg p-4">
          <div className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Valor Pipeline</div>
          <div className="text-3xl font-mono text-white">${(stats?.total_value || 0).toLocaleString()}</div>
        </div>
        <div className="bg-[#13151A] border border-zinc-800 rounded-lg p-4">
          <div className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Tasa Conversión</div>
          <div className="text-3xl font-mono text-emerald-400">{stats?.conversion_rate || 0}%</div>
        </div>
        <div className="bg-[#13151A] border border-zinc-800 rounded-lg p-4">
          <div className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Ganados</div>
          <div className="text-3xl font-mono text-white">{stats?.by_status?.won?.count || 0}</div>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 overflow-x-auto">
        {statuses.map((status) => {
          const statusLeads = leads.filter((l) => l.status === status.id);
          return (
            <div key={status.id} className="min-w-[200px]">
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-3 h-3 rounded-full ${status.color}`} />
                <span className="text-zinc-400 text-sm font-medium">{status.label}</span>
                <span className="text-zinc-600 text-xs">({statusLeads.length})</span>
              </div>
              <div className="space-y-2 min-h-[200px] bg-[#0A0A0A] rounded-lg p-2 border border-zinc-800">
                {statusLeads.map((lead) => (
                  <div
                    key={lead.id}
                    className="bg-[#13151A] border border-zinc-800 rounded-lg p-3 cursor-pointer hover:border-zinc-600 transition-colors"
                    data-testid={`lead-card-${lead.id}`}
                  >
                    <div className="text-white font-medium text-sm mb-1">{lead.name}</div>
                    <div className="text-zinc-500 text-xs mb-2">{lead.company || lead.email}</div>
                    {lead.value > 0 && (
                      <div className="text-emerald-400 text-xs font-mono">${lead.value.toLocaleString()}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Lead Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#13151A] border border-zinc-800 rounded-xl w-full max-w-md p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={() => setShowAddModal(false)}
              className="absolute top-4 right-4 text-zinc-500 hover:text-white"
            >
              ✕
            </button>
            <h2 className="text-xl font-medium text-white mb-6">Agregar Lead</h2>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Nombre *"
                value={newLead.name}
                onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-zinc-800 rounded-lg text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-white"
                data-testid="add-lead-name"
              />
              <input
                type="email"
                placeholder="Email *"
                value={newLead.email}
                onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-zinc-800 rounded-lg text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-white"
                data-testid="add-lead-email"
              />
              <input
                type="tel"
                placeholder="Teléfono"
                value={newLead.phone}
                onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-zinc-800 rounded-lg text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-white"
              />
              <input
                type="text"
                placeholder="Empresa"
                value={newLead.company}
                onChange={(e) => setNewLead({ ...newLead, company: e.target.value })}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-zinc-800 rounded-lg text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-white"
              />
              <input
                type="number"
                placeholder="Valor estimado ($)"
                value={newLead.value || ""}
                onChange={(e) => setNewLead({ ...newLead, value: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-zinc-800 rounded-lg text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-white"
              />
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 py-3 border border-zinc-800 text-zinc-400 rounded-lg hover:bg-zinc-800 transition-colors"
                  data-testid="cancel-add-lead"
                >
                  Cancelar
                </button>
                <button
                  onClick={addLead}
                  className="flex-1 py-3 bg-white text-black rounded-lg font-medium hover:bg-zinc-200 transition-colors"
                  data-testid="submit-add-lead"
                >
                  Agregar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Calendar and Editor pages use dedicated components
const CalendarPage = () => <CalendarSection />;
const EditorPage = () => <VisualEditor />;

// Main Platform Layout
const PlatformDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <PlatformSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

      {/* Main Content */}
      <div className="ml-64">
        {/* Ticker */}
        <MetricTicker />

        {/* Header */}
        <header className="sticky top-0 z-30 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-zinc-800 px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden p-2 hover:bg-zinc-800 rounded-lg">
                <List size={24} className="text-white" />
              </button>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-white font-medium text-sm">{user?.name || "Admin"}</p>
                <p className="text-zinc-500 text-xs">{user?.email}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0)?.toUpperCase() || "A"}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-8">
          <Routes>
            <Route index element={<DashboardPage />} />
            <Route path="ai" element={<AIGeneratorPage />} />
            <Route path="crm" element={<CRMPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="editor" element={<EditorPage />} />
            <Route path="*" element={<DashboardPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default PlatformDashboard;
